import React, { useEffect, useMemo, useRef, useState } from 'react'
import { IOwner } from '@features/account/types'
import { TFormModel, useForm } from '@shared/hooks/useForm'
import {
  PROFILE_ADDRESS_SECTION,
  PROFILE_CANCEL_BUTTON_TEXT,
  PROFILE_CHECK_ADDRESS_HELP_TEXT,
  PROFILE_CHECK_YOUR_ADDRESS,
  PROFILE_EMAIL_ADDRESS_SECTION,
  PROFILE_ERROR_SAVING_PROFILE,
  PROFILE_KEEP_MY_ENTERED_ADDRESS,
  PROFILE_INVALID_POSTAL_CODE_MESSAGE,
  INVALID_PHONE_FORMAT_MESSAGE,
  PROFILE_PHONE_NUMBER_SECTION,
  PROFILE_SAVE_ADDRESS_BUTTON_TEXT,
  PROFILE_SAVE_BUTTON_TEXT,
  PROFILE_REQUEST_FAILED_TRY_AGAIN,
  PROFILE_SAVE_NOT_CONFIRMED_MESSAGE,
  PROFILE_SAVE_SUCCESS_MESSAGE,
  PROFILE_SAVING_BUTTON_TEXT,
  PROFILE_USE_SUGGESTED_ADDRESS,
  SELECT_COUNTRY,
  SELECT_STATE_PROVINCE,
  SNACKBAR_DURATION_SECONDS,
} from '@shared/constants'
import './EditInfoForm.scss'
import { ZinButton } from '@tnl/zin-react'
import { useCountryStateOptions } from '@hooks/useCountryState'
import { customFormValidators } from '@utils/customFormValidators'
import { PostalCodeLocale } from 'validator'
import FormField from '@shared/components/form/FormField'
import { updateAccountInfo } from '@features/account/services'
import { useAccountContext } from '@features/account/contexts/core'
import type { IMkOwnerUpdateResponse, IMkOwnerUpdateParameters } from '@account/types/editInfoForm'
import { areAddressDoctorChoicesEquivalent, scrollWindowToTop } from '@account/utils/addressDoctorUtils'
import { DEFAULT_ACCOUNT_COUNTRY_CODE, preprocessPhone } from '@account/utils/preprocessAccountPhone'
import { findProfileSaveMismatches, type IExpectedProfileSnapshot } from '@account/utils/verifyProfileSave'
import { useSnackbar } from '@shared/contexts/SnackbarContext'
import { useAuthHeaders } from '@shared/hooks'
import { getCountryName } from '@utils/getCountryName'
import { formatStateProvinceForDisplay, getStateCode, getStateName } from '@utils/formattingUtils'
import { RadioButton } from 'primereact/radiobutton'

export interface IEditInfoFormProps {
  owner: IOwner
  onCancel: () => void
  onSave: () => void
}

const EditInfoForm: React.FC<IEditInfoFormProps> = ({ owner, onCancel, onSave }) => {
  if (!owner) return null

  const initialFormModel = useMemo<TFormModel>(
    () => ({
      fields: {
        emailAddress: {
          id: 'emailAddress',
          label: 'Email Address',
          name: 'emailAddress',
          required: true,
          type: 'email',
        },
        country: {
          clearsOtherFields: ['stateProvince'],
          id: 'country',
          label: 'Country',
          name: 'country',
          options: [],
          required: true,
          type: 'select',
        },
        addressLine1: {
          id: 'addressLine1',
          label: 'Address Line 1',
          name: 'addressLine1',
          required: true,
          type: 'text',
        },
        addressLine2: {
          id: 'addressLine2',
          label: 'Address Line 2',
          name: 'addressLine2',
          type: 'text',
        },
        addressLine3: {
          id: 'addressLine3',
          label: 'Address Line 3',
          name: 'addressLine3',
          type: 'text',
        },
        city: {
          id: 'city',
          label: 'City',
          name: 'city',
          required: true,
          type: 'text',
        },
        stateProvince: {
          id: 'stateProvince',
          label: 'State / Province',
          name: 'stateProvince',
          options: [],
          required: true,
          type: 'select',
        },
        postalCode: {
          id: 'postalCode',
          label: 'Postal Code',
          name: 'postalCode',
          required: true,
          type: 'text',
          validators: [customFormValidators.isPostalCode(DEFAULT_ACCOUNT_COUNTRY_CODE, PROFILE_INVALID_POSTAL_CODE_MESSAGE)],
        },
        phoneHome: {
          id: 'phoneHome',
          label: 'Home Phone',
          name: 'phoneHome',
          type: 'tel',
          validators: [customFormValidators.isInternationalPhone(INVALID_PHONE_FORMAT_MESSAGE)],
        },
        phoneMobile: {
          id: 'phoneMobile',
          label: 'Mobile Phone',
          name: 'phoneMobile',
          type: 'tel',
          validators: [customFormValidators.isInternationalPhone(INVALID_PHONE_FORMAT_MESSAGE)],
        },
        phoneWork: {
          id: 'phoneWork',
          label: 'Work Phone',
          name: 'phoneWork',
          type: 'tel',
          validators: [customFormValidators.isInternationalPhone(INVALID_PHONE_FORMAT_MESSAGE)],
        },
      },
      initialState: {
        emailAddress: owner.emailAddress || '',
        confirmEmailAddress: '',
        country: owner.country || '',
        addressLine1: owner.addressLine1 || '',
        addressLine2: owner.addressLine2 || '',
        addressLine3: owner.addressLine3 || '',
        city: owner.city || '',
        stateProvince: owner.stateProvince || '',
        postalCode: owner.postalCode || '',
        phoneHome: owner.phoneHome || '',
        phoneMobile: owner.phoneMobile || '',
        phoneWork: owner.phoneWork || '',
      },
    }),
    [owner],
  )

  const [formModel, setFormModel] = useState<TFormModel>(initialFormModel)
  const form = useForm(formModel)
  const [isSaving, setIsSaving] = useState(false)
  const [response, setResponse] = React.useState<Partial<IMkOwnerUpdateResponse>>({})
  const [addressChoice, setAddressChoice] = React.useState('')
  const addressDoctorRef = useRef<HTMLFormElement>(null)
  const { showSnackbar } = useSnackbar()
  const authHeaders = useAuthHeaders()
  const { refreshProfile } = useAccountContext()

  const selectedCountry: PostalCodeLocale = React.useMemo(() => form.values.country || '', [form.values])
  const { countries, states } = useCountryStateOptions(selectedCountry)

  // Stable, reactive country options
  const countryOptions = useMemo(() => {
    if (!countries?.length) {
      return [
        {
          label: SELECT_COUNTRY,
          value: '',
          disabled: true,
        },
      ]
    }
    return [
      {
        label: SELECT_COUNTRY,
        value: '',
        disabled: true,
      },
      ...countries.map(({ countryCode, countryName }) => ({
        label: countryName,
        value: countryCode,
      })),
    ]
  }, [countries])

  // Stable, reactive state/province options
  const stateProvinceOptions = useMemo(() => {
    if (!states?.length) {
      return [
        {
          label: SELECT_STATE_PROVINCE,
          value: '',
          disabled: true,
        },
      ]
    }
    return [
      {
        label: SELECT_STATE_PROVINCE,
        value: '',
        disabled: true,
      },
      ...states.map(({ value, name }) => ({
        label: name,
        value,
      })),
    ]
  }, [states])

  // Options use state `value` (e.g. AZ); owner/API may send full name or odd Voyager strings — coerce to `value`.
  useEffect(() => {
    if (!states?.length) return
    const raw = form.values.stateProvince
    if (raw === undefined || raw === null || String(raw).trim() === '') return
    if (states.some((s) => s.value === raw)) return

    const code = getStateCode(states, String(raw))
    if (code && code !== raw) {
      form.setValue('stateProvince', code)
    }
  }, [states, form.values.stateProvince, form.setValue])

  // To avoid address suggestions if the address has not changed.
  const hasAddressChanged = useMemo(() => {
    const initial = initialFormModel.initialState
    const current = form.values
    return !areAddressDoctorChoicesEquivalent(
      {
        country: initial.country,
        addressLine1: initial.addressLine1,
        addressLine2: initial.addressLine2,
        addressLine3: initial.addressLine3,
        city: initial.city,
        stateProvince: initial.stateProvince,
        postalCode: initial.postalCode,
      },
      {
        country: current.country,
        addressLine1: current.addressLine1,
        addressLine2: current.addressLine2,
        addressLine3: current.addressLine3,
        city: current.city,
        stateProvince: current.stateProvince,
        postalCode: current.postalCode,
      },
      states,
    )
  }, [
    initialFormModel.initialState,
    form.values.country,
    form.values.addressLine1,
    form.values.addressLine2,
    form.values.addressLine3,
    form.values.city,
    form.values.stateProvince,
    form.values.postalCode,
    states,
  ])

  useEffect(() => {
    if (hasAddressChanged && response.suggestedAddress && response.enteredAddress) {
      addressDoctorRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' })
    }
  }, [hasAddressChanged, response.suggestedAddress, response.enteredAddress])

  // Dynamically update formModel when countries are fetched
  React.useEffect(() => {
    setFormModel((prev) => ({
      ...prev,
      fields: {
        ...prev.fields,
        country: {
          ...prev.fields.country,
          options: countryOptions, // or countryOptions
        },
      },
    }))
  }, [countryOptions])

  // Dynamically update formModel when selectedCountry or stateProvinceOptions changes
  React.useEffect(() => {
    setFormModel((prev) => ({
      ...prev,
      fields: {
        ...prev.fields,
        stateProvince: {
          ...prev.fields.stateProvince,
          options: stateProvinceOptions,
        },
        postalCode: {
          ...prev.fields.postalCode,
          validators: [customFormValidators.isPostalCode(selectedCountry, PROFILE_INVALID_POSTAL_CODE_MESSAGE)],
        },
      },
    }))
  }, [selectedCountry, stateProvinceOptions])

  const basePayload = {
    addressId: owner.addressId,
    addressPk: owner.addressPk,
    emailPk: owner.emailPk,
  }

  const buildProfileUpdatePayload = (suggestAddress: boolean) => ({
    ...basePayload,
    addressLine1: form?.values?.addressLine1,
    addressLine2: form?.values?.addressLine2,
    addressLine3: form?.values?.addressLine3,
    country: getCountryName(form?.values?.country) ? form?.values?.country : '',
    countryCode: form?.values?.country,
    city: form?.values?.city,
    emailAddress: form?.values?.emailAddress,
    phoneHome: preprocessPhone(form?.values?.phoneHome, form?.values?.country || DEFAULT_ACCOUNT_COUNTRY_CODE, owner.phoneHome),
    phoneMobile: preprocessPhone(form?.values?.phoneMobile, form?.values?.country || DEFAULT_ACCOUNT_COUNTRY_CODE, owner.phoneMobile),
    phoneWork: preprocessPhone(form?.values?.phoneWork, form?.values?.country || DEFAULT_ACCOUNT_COUNTRY_CODE, owner.phoneWork),
    postalCode: form?.values?.postalCode,
    stateProvince: getStateName(states, form?.values?.stateProvince || '') || form?.values?.stateProvince || '',
    stateProvinceCode: getStateCode(states, form?.values?.stateProvince || ''),
    suggestAddress,
  })

  /**
   * Voyager owner-services /contacts can return success even when only some fields persisted, and
   * mkback now also treats its 400 responses as "may have persisted" so the client can decide
   * (see VgerOwnServContactsAdapter). After every save we GET the profile and compare the values
   * the user submitted to what the server now has — if anything we tried to change did not stick
   * we surface a "could not confirm" snackbar instead of the success copy.
   */
  const verifyAndNotify = async (
    payload: Pick<
      IMkOwnerUpdateParameters,
      | 'emailAddress'
      | 'addressLine1'
      | 'addressLine2'
      | 'addressLine3'
      | 'city'
      | 'countryCode'
      | 'stateProvince'
      | 'postalCode'
      | 'phoneHome'
      | 'phoneMobile'
      | 'phoneWork'
    >,
  ) => {
    const expected: IExpectedProfileSnapshot = {
      emailAddress: payload.emailAddress,
      addressLine1: payload.addressLine1,
      addressLine2: payload.addressLine2,
      addressLine3: payload.addressLine3,
      city: payload.city,
      country: payload.countryCode,
      stateProvince: payload.stateProvince,
      postalCode: payload.postalCode,
      phoneHome: payload.phoneHome ? { phoneNumber: payload.phoneHome.phoneNumber, countryCode: payload.phoneHome.countryCode } : undefined,
      phoneMobile: payload.phoneMobile ? { phoneNumber: payload.phoneMobile.phoneNumber, countryCode: payload.phoneMobile.countryCode } : undefined,
      phoneWork: payload.phoneWork ? { phoneNumber: payload.phoneWork.phoneNumber, countryCode: payload.phoneWork.countryCode } : undefined,
    }
    // refreshProfile both refetches from mkback AND updates the account context, so the parent
    // ProfileInfoBox does not need to do its own GET when the dialog closes.
    let fresh = null
    try {
      fresh = await refreshProfile()
    } catch (e) {
      console.error('verifyAndNotify: refreshProfile failed', e)
    }
    const mismatches = findProfileSaveMismatches(expected, fresh)
    if (mismatches.length === 0) {
      showSnackbar({
        message: PROFILE_SAVE_SUCCESS_MESSAGE,
        type: 'success',
        duration: SNACKBAR_DURATION_SECONDS,
      })
    } else {
      console.warn(
        'Profile save not confirmed; mismatched fields:',
        mismatches.map((m) => m.field),
      )
      showSnackbar({
        message: PROFILE_SAVE_NOT_CONFIRMED_MESSAGE,
        type: 'error',
        duration: SNACKBAR_DURATION_SECONDS,
      })
    }
  }

  const handleSubmit = () => {
    setIsSaving(true)
    const initialPayload = buildProfileUpdatePayload(hasAddressChanged)
    updateAccountInfo(initialPayload, authHeaders)
      // eslint-disable-next-line @typescript-eslint/no-shadow
      .then(async (resp) => {
        if (resp == null) {
          showSnackbar({
            message: PROFILE_REQUEST_FAILED_TRY_AGAIN,
            type: 'error',
            duration: SNACKBAR_DURATION_SECONDS,
          })
          return
        }
        if (resp.updated) {
          setResponse({})
          scrollWindowToTop()
          await verifyAndNotify(initialPayload)
          onSave()
        } else if (resp.suggestedAddress && resp.enteredAddress && areAddressDoctorChoicesEquivalent(resp.suggestedAddress, resp.enteredAddress, states)) {
          const confirmPayload = buildProfileUpdatePayload(false)
          return updateAccountInfo(confirmPayload, authHeaders).then(async (confirmResp) => {
            if (confirmResp == null) {
              showSnackbar({
                message: PROFILE_REQUEST_FAILED_TRY_AGAIN,
                type: 'error',
                duration: SNACKBAR_DURATION_SECONDS,
              })
              return
            }
            if (confirmResp.updated) {
              setResponse({})
              scrollWindowToTop()
              await verifyAndNotify(confirmPayload)
              onSave()
            } else {
              setResponse(confirmResp)
            }
          })
        } else {
          setResponse(resp)
        }
      })
      .catch((error) => {
        console.error(`${PROFILE_ERROR_SAVING_PROFILE}: ${error}`)
        showSnackbar({
          message: PROFILE_REQUEST_FAILED_TRY_AGAIN,
          type: 'error',
          duration: SNACKBAR_DURATION_SECONDS,
        })
      })
      .finally(() => {
        setIsSaving(false)
      })
  }

  const handleChooseAddress = (e: React.FormEvent) => {
    e.preventDefault()
    setIsSaving(true)

    const chosen = addressChoice === 'suggestedAddress' ? response.suggestedAddress : response.enteredAddress
    if (!chosen) {
      setIsSaving(false)
      return
    }

    const rawState = chosen.stateProvince || ''

    const chosenPayload = {
      ...basePayload,
      addressLine1: chosen.addressLine1 || '',
      addressLine2: chosen.addressLine2 || '',
      addressLine3: chosen.addressLine3 || '',
      country: getCountryName(form?.values?.country) ? form?.values?.country : '',
      countryCode: form?.values?.country,
      city: chosen.city || '',
      emailAddress: form?.values?.emailAddress,
      phoneHome: preprocessPhone(form?.values?.phoneHome, form?.values?.country || DEFAULT_ACCOUNT_COUNTRY_CODE, owner.phoneHome),
      phoneMobile: preprocessPhone(form?.values?.phoneMobile, form?.values?.country || DEFAULT_ACCOUNT_COUNTRY_CODE, owner.phoneMobile),
      phoneWork: preprocessPhone(form?.values?.phoneWork, form?.values?.country || DEFAULT_ACCOUNT_COUNTRY_CODE, owner.phoneWork),
      postalCode: chosen.postalCode || '',
      stateProvince: getStateName(states, rawState) || rawState,
      stateProvinceCode: getStateCode(states, rawState),
      suggestAddress: false,
    }

    updateAccountInfo(chosenPayload, authHeaders)
      .then(async (response) => {
        if (response == null) {
          showSnackbar({
            message: PROFILE_REQUEST_FAILED_TRY_AGAIN,
            type: 'error',
            duration: SNACKBAR_DURATION_SECONDS,
          })
          return
        }
        scrollWindowToTop()
        await verifyAndNotify(chosenPayload)
        setResponse({})
        onSave()
      })
      .catch((error) => {
        console.error('Error saving address:', error)
        showSnackbar({
          message: PROFILE_REQUEST_FAILED_TRY_AGAIN,
          type: 'error',
          duration: SNACKBAR_DURATION_SECONDS,
        })
      })
      .finally(() => {
        setIsSaving(false)
      })
  }

  return (
    <div className="account-edit-info">
      {!response.suggestedAddress && (
        <form
          key={owner.id || 'form'}
          name="editInfoForm"
          noValidate
          onSubmit={form.handleSubmit(handleSubmit)}
          ref={form.formRef}>
          <div className="account-edit-info__sections">
            <div className="account-edit-info__section">
              <h2 className="account-edit-info__section-title body-2 font-bold">{PROFILE_EMAIL_ADDRESS_SECTION}</h2>
              <div className="account-edit-info__section-fields">
                <FormField
                  field={formModel.fields.emailAddress}
                  form={form}
                />
              </div>
            </div>
            <div className="account-edit-info__section">
              <h2 className="account-edit-info__section-title body-2 font-bold">{PROFILE_ADDRESS_SECTION}</h2>
              <div className="account-edit-info__section-fields">
                <FormField
                  field={formModel.fields.country}
                  form={form}
                />
                <FormField
                  field={formModel.fields.addressLine1}
                  form={form}
                />
                <FormField
                  field={formModel.fields.addressLine2}
                  form={form}
                />
                <FormField
                  field={formModel.fields.addressLine3}
                  form={form}
                />
                <FormField
                  field={formModel.fields.city}
                  form={form}
                />
                <FormField
                  field={formModel.fields.stateProvince}
                  form={form}
                />
                <FormField
                  field={formModel.fields.postalCode}
                  form={form}
                />
              </div>
            </div>
            <div className="account-edit-info__section">
              <h2 className="account-edit-info__section-title body-2 font-bold">{PROFILE_PHONE_NUMBER_SECTION}</h2>
              <div className="account-edit-info__section-fields">
                <FormField
                  field={formModel.fields.phoneHome}
                  form={form}
                />
                <FormField
                  field={formModel.fields.phoneMobile}
                  form={form}
                />
                <FormField
                  field={formModel.fields.phoneWork}
                  form={form}
                />
              </div>
            </div>
            <div className="account-edit-info__actions">
              <ZinButton
                onClick={onCancel}
                type="button"
                variant="secondary">
                {PROFILE_CANCEL_BUTTON_TEXT}
              </ZinButton>
              <ZinButton
                disabled={isSaving}
                type="submit">
                {isSaving ? PROFILE_SAVING_BUTTON_TEXT : PROFILE_SAVE_BUTTON_TEXT}
              </ZinButton>
            </div>
          </div>
        </form>
      )}
      {/* Address Doctor Embedded Overlay */}
      {hasAddressChanged && response.suggestedAddress && response.enteredAddress && (
        <form
          ref={addressDoctorRef}
          className="account-edit-info__address-doctor"
          onSubmit={handleChooseAddress}>
          <div>
            <h2 className="font-bold">{PROFILE_CHECK_YOUR_ADDRESS}</h2>
            <p>{PROFILE_CHECK_ADDRESS_HELP_TEXT}</p>
          </div>
          <div className="account-edit-info__address-doctor-list">
            {response.suggestedAddress && (
              <div className="account-edit-info__address-doctor-address">
                <h3>{PROFILE_USE_SUGGESTED_ADDRESS}</h3>
                <div className="account-edit-info__address-doctor-radio">
                  <div className="account-edit-info__address-doctor-radio-field">
                    <RadioButton
                      checked={addressChoice === 'suggestedAddress'}
                      inputId="suggestedAddress"
                      name="addressChoice"
                      onChange={() => setAddressChoice('suggestedAddress')}
                      required
                      value="suggestedAddress"
                    />
                  </div>
                  <div className="account-edit-info__address-doctor-radio-label">
                    {/* eslint-disable-next-line jsx-a11y/label-has-associated-control */}
                    <label htmlFor="suggestedAddress">
                      {response.suggestedAddress.addressLine1 && <div>{response.suggestedAddress.addressLine1}</div>}
                      {response.suggestedAddress.addressLine2 && <div>{response.suggestedAddress.addressLine2}</div>}
                      {response.suggestedAddress.addressLine3 && <div>{response.suggestedAddress.addressLine3}</div>}
                      <div>
                        {response.suggestedAddress.city}, {formatStateProvinceForDisplay(states, response.suggestedAddress.stateProvince)} {response.suggestedAddress.postalCode}
                      </div>
                    </label>
                  </div>
                </div>
              </div>
            )}
            {response.enteredAddress && (
              <div className="account-edit-info__address-doctor-address">
                <h3>{PROFILE_KEEP_MY_ENTERED_ADDRESS}</h3>
                <div className="account-edit-info__address-doctor-radio">
                  <div className="account-edit-info__address-doctor-radio-field">
                    <RadioButton
                      checked={addressChoice === 'enteredAddress'}
                      inputId="enteredAddress"
                      name="addressChoice"
                      onChange={() => setAddressChoice('enteredAddress')}
                      required
                      value="enteredAddress"
                    />
                  </div>
                  <div className="account-edit-info__address-doctor-radio-label">
                    {/* eslint-disable-next-line jsx-a11y/label-has-associated-control */}
                    <label htmlFor="enteredAddress">
                      {response.enteredAddress.addressLine1 && <div>{response.enteredAddress.addressLine1}</div>}
                      {response.enteredAddress.addressLine2 && <div>{response.enteredAddress.addressLine2}</div>}
                      {response.enteredAddress.addressLine3 && <div>{response.enteredAddress.addressLine3}</div>}
                      <div>
                        {response.enteredAddress.city}, {formatStateProvinceForDisplay(states, response.enteredAddress.stateProvince)} {response.enteredAddress.postalCode}
                      </div>
                    </label>
                  </div>
                </div>
              </div>
            )}
            <div className="account-edit-info__address-doctor-actions">
              <ZinButton
                disabled={!addressChoice || isSaving}
                onClick={handleChooseAddress}>
                {isSaving ? PROFILE_SAVING_BUTTON_TEXT : PROFILE_SAVE_ADDRESS_BUTTON_TEXT}
              </ZinButton>
            </div>
          </div>
        </form>
      )}
    </div>
  )
}

export default EditInfoForm
